// src/pages/admin/bookings/components/BookingCustomerCard.tsx

import * as React from "react";
import { Box, Divider, Paper, Stack, Typography, Chip, IconButton, Tooltip } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";

import type { BookingCustomer } from "../bookingTypes";
import { safeStr } from "../bookingTypes";

async function copyToClipboard(text: string) {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch {
        return false;
    }
}

export default function BookingCustomerCard({
    customer,
    fallbackFullName,
    fallbackEmail,
    fallbackPhone,
}: {
    customer?: BookingCustomer | null;
    fallbackFullName?: string | null;
    fallbackEmail?: string | null;
    fallbackPhone?: string | null;
}) {
    const name = safeStr(customer?.full_name, safeStr(fallbackFullName, "—"));
    const email = safeStr(customer?.email, safeStr(fallbackEmail, ""));
    const phone = safeStr(customer?.phone, safeStr(fallbackPhone, ""));

    const customerId = customer?.id ?? null;

    const [copied, setCopied] = React.useState<string | null>(null);

    const onCopy = async (value: string, label: string) => {
        if (!value) return;
        const ok = await copyToClipboard(value);
        if (ok) {
            setCopied(label);
            window.setTimeout(() => setCopied(null), 1200);
        }
    };

    return (
        <Paper variant="outlined" sx={{ borderRadius: 3, p: 2, height: "100%" }}>
            <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1} flexWrap="wrap">
                    <Typography variant="subtitle1" sx={{ fontWeight: 950 }}>
                        Customer
                    </Typography>

                    <Stack direction="row" spacing={1} alignItems="center">
                        {customerId ? (
                            <Chip
                                size="small"
                                variant="outlined"
                                label={`ID: ${customerId}`}
                                sx={{ borderRadius: 999, fontWeight: 800 }}
                            />
                        ) : (
                            <Chip
                                size="small"
                                variant="outlined"
                                label="No customer record"
                                sx={{ borderRadius: 999, fontWeight: 800 }}
                            />
                        )}
                    </Stack>
                </Stack>

                <Divider />

                <Stack spacing={0.75}>
                    <Typography variant="body1" sx={{ fontWeight: 900 }}>
                        {name}
                    </Typography>

                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Typography variant="body2" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                            <EmailIcon fontSize="small" />
                            {email || "—"}
                        </Typography>

                        {email ? (
                            <Stack direction="row" spacing={0.5} alignItems="center">
                                <Tooltip title={copied === "email" ? "Copied!" : "Copy email"}>
                                    <IconButton size="small" onClick={() => onCopy(email, "email")}>
                                        <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Compose email">
                                    <IconButton size="small" component="a" href={`mailto:${encodeURIComponent(email)}`}>
                                        <OpenExternal />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        ) : null}
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Typography variant="body2" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                            <PhoneIcon fontSize="small" />
                            {phone || "—"}
                        </Typography>

                        {phone ? (
                            <Stack direction="row" spacing={0.5} alignItems="center">
                                <Tooltip title={copied === "phone" ? "Copied!" : "Copy phone"}>
                                    <IconButton size="small" onClick={() => onCopy(phone, "phone")}>
                                        <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Call">
                                    <IconButton size="small" component="a" href={`tel:${encodeURIComponent(phone)}`}>
                                        <OpenExternal />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        ) : null}
                    </Stack>

                    <Box sx={{ mt: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                            Tip: copy details with one click.
                        </Typography>
                    </Box>
                </Stack>
            </Stack>
        </Paper>
    );
}

/**
 * Small internal icon (to avoid importing another MUI icon for a tiny action button).
 */
function OpenExternal() {
    return (
        <Box
            component="span"
            aria-hidden
            sx={{
                width: 18,
                height: 18,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 999,
                border: "1px solid",
                borderColor: "divider",
                fontSize: 11,
                fontWeight: 900,
                lineHeight: 1,
            }}
        >
            ↗
        </Box>
    );
}
